/**
 * @module UserFormUtils
 *
 * Pure utility functions extracted from `UserFormDialog.vue` for testability.
 *
 * The `UserFormDialog` component is responsible for creating and editing users
 * in the IRES Office portal. It handles:
 * - User CRUD operations (create, read, update, soft-delete)
 * - Form validation (nickname, email, staff number, role, password, etc.)
 * - Shared email address flows (joining / leaving shared accounts)
 * - Vehicle allocation management
 * - Branch-based filtering of vehicles
 * - Licence selection (V2 manual vs V3 role-derived)
 * - Multi-client "shared user" membership for client groups
 * - User blocking / unblocking
 * - Password reset (individual and shared-email propagation)
 * - Language preference management
 *
 * This file extracts the deterministic, side-effect-free logic so it can be
 * unit-tested without mounting the Vue component or mocking Firebase.
 */

import Vehicle from "@/data/models/Vehicle";
import { Branch } from "@/data/models/Branch";
import { ClientsInfoOfGroup } from "@/data/models/Client";
import { GeoLocation } from "@/utils/GeocodingService";

/**
 * Minimal user shape needed by utility functions.
 * Avoids importing the full User model which has transitive Vuex/Firebase
 * dependencies that make unit testing difficult.
 */
export interface UserFormUser {
  id: string;
  nickname: string;
  vehicle?: string;
  sharedUserOfGroup?: boolean;
  sharedEmailAddressId?: string | null;
}

/**
 * Re-exported enums that match the values in `@/data/models/User`.
 * Duplicated here to break the import chain for testability.
 */
export enum Role {
  ADMIN = "ADMIN",
  OFFICE = "OFFICE",
  PROJECT_MANAGER = "PROJECT_MANAGER",
  TECHNICIAN_AND_LEAK_DETECTION = "TECHNICIAN_AND_LEAK_DETECTION",
  LEAK_DETECTION = "LEAK_DETECTION",
  TECHNICIAN_1 = "TECHNICIAN_1",
  TECHNICIAN_2 = "TECHNICIAN_2",
  CUSTOM_ROLE_1 = "CUSTOM_ROLE_1",
  CUSTOM_ROLE_2 = "CUSTOM_ROLE_2",
  CUSTOM_ROLE_3 = "CUSTOM_ROLE_3",
  CUSTOM_ROLE_4 = "CUSTOM_ROLE_4",
  CUSTOM_ROLE_5 = "CUSTOM_ROLE_5",
}

export enum ValidationLevel {
  LOW = "LOW",
  NORMAL = "NORMAL",
  STRICT = "STRICT",
  NOT_SPECIFIED = "NOT_SPECIFIED",
}

/**
 * Deep-equality comparison for two arrays (order matters).
 * Inlined to avoid importing Utils.ts which may pull in heavy dependencies.
 */
function arraysEqual<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Whether two positions differ, treating null and undefined as the same "not set".
 *
 * Compares the numbers directly rather than a formatted string. The display form used to
 * round-trip through `"50.10 / 6.9"`, where a stored `50.10` and a re-parsed `50.1` read
 * as a change and left SICHERN enabled on a form nobody had edited.
 *
 * @param a - one position, or null/undefined for none.
 * @param b - the other.
 * @returns `true` if they differ in position or in quality.
 */
export function hasCoordinatesChanged(
  a: GeoLocation | null | undefined,
  b: GeoLocation | null | undefined
): boolean {
  if (!a || !b) {
    return !!a !== !!b;
  }

  return (
    a.latitude !== b.latitude ||
    a.longitude !== b.longitude ||
    (a.quality ?? null) !== (b.quality ?? null)
  );
}

// ─── Validation ──────────────────────────────────────────────────────

/** Fields needed for form validation. */
export interface UserFormFields {
  email: string;
  nickname: string;
  role: Role | null;
  validationLevel: ValidationLevel;
  staffNumber: string;
  temporaryPassword: string;
  supervisorId: string | null;
  startedForUserCreation: boolean;
  resetUserPassword: boolean;
  isAllowedToSkipUserDataValidation: boolean;
  isSupervisorValid: boolean;
}

/** Validation error keys returned per field. */
export interface UserFormValidationErrors {
  emailError: string[];
  nicknameError: string[];
  roleError: string[];
  validationLevelError: string[];
  staffNumberError: string[];
  temporaryPasswordError: string[];
  supervisorError: string[];
}

/**
 * Validates user form fields and returns per-field error arrays.
 * All error strings are i18n keys; the caller must resolve them.
 *
 * @param fields - Current form field values.
 * @returns Object with `isValid` flag and per-field error arrays.
 */
export function validateUserForm(fields: UserFormFields): {
  isValid: boolean;
  errors: UserFormValidationErrors;
} {
  const errors: UserFormValidationErrors = {
    emailError: [],
    nicknameError: [],
    roleError: [],
    validationLevelError: [],
    staffNumberError: [],
    temporaryPasswordError: [],
    supervisorError: [],
  };

  let isValid = true;

  if (!fields.email || !fields.email.trim()) {
    errors.emailError = ["users.error.missingEmailAddress"];
    isValid = false;
  }

  if (!fields.nickname.trim() && !fields.isAllowedToSkipUserDataValidation) {
    errors.nicknameError = ["users.error.missingNickname"];
    isValid = false;
  }

  if (fields.validationLevel === ValidationLevel.NOT_SPECIFIED) {
    errors.validationLevelError = ["users.error.missingValidationLevel"];
    isValid = false;
  }

  if (!fields.role && !fields.isAllowedToSkipUserDataValidation) {
    errors.roleError = ["users.error.missingRole"];
    isValid = false;
  }

  if (
    (fields.startedForUserCreation || fields.resetUserPassword) &&
    fields.temporaryPassword.length < 8
  ) {
    errors.temporaryPasswordError = ["users.error.passwordToShort"];
    isValid = false;
  }

  if (!fields.staffNumber && !fields.isAllowedToSkipUserDataValidation) {
    errors.staffNumberError = ["users.error.missingStaffNumber"];
    isValid = false;
  }

  if (fields.supervisorId && !fields.isSupervisorValid) {
    errors.supervisorError = ["users.error.invalidSupervisor"];
    isValid = false;
  }

  return { isValid, errors };
}

// ─── Branch / Vehicle helpers ────────────────────────────────────────

/**
 * Resolves a branch number (string or numeric) to a branch ID
 * by matching against the client's branch list.
 *
 * @returns The branch ID if exactly one match is found, otherwise `null`.
 */
export function resolveBranchIdFromNumber(
  branchNumber: string | number | null | undefined,
  allBranches: Branch[]
): string | null {
  if (!branchNumber) {
    return null;
  }

  const matches = allBranches.filter(
    (branch) => branchNumber.toString() === branch.branchNumber.toString()
  );

  return matches.length === 1 ? matches[0].id : null;
}

/**
 * Filters vehicles to only those whose branch matches one of the user's
 * branches. If the vehicle has no branch, or the user has no branches
 * assigned, the vehicle is included (no filtering).
 *
 * @param vehicles - All vehicles of the client.
 * @param userBranches - Branch IDs assigned to the user being edited.
 * @param allBranches - All branches of the client (for number→ID resolution).
 * @returns Vehicles available for assignment to this user.
 */
export function filterVehiclesByUserBranches(
  vehicles: Vehicle[],
  userBranches: string[] | undefined,
  allBranches: Branch[]
): Vehicle[] {
  return vehicles.filter((vehicle) => {
    const vehicleBranchId = resolveBranchIdFromNumber(vehicle.branch, allBranches);

    if (!vehicleBranchId || !userBranches || userBranches.length === 0) {
      return true;
    }

    return userBranches.includes(vehicleBranchId);
  });
}

/**
 * Finds the Vehicle object assigned to a user by matching `user.vehicle`
 * (an ID string) against the vehicles list.
 *
 * @returns The matching Vehicle or `null`.
 */
export function getUserVehicle(
  user: UserFormUser | null | undefined,
  vehiclesOfClient: Vehicle[]
): Vehicle | null {
  if (!user) {
    return null;
  }
  return vehiclesOfClient.find((v) => v.id === user.vehicle) ?? null;
}

// ─── Change detection ────────────────────────────────────────────────

/** Snapshot of form state used for change detection. */
export interface UserFormSnapshot {
  nickname: string;
  firstName: string;
  lastName: string;
  role: Role | null;
  wasSetToAdmin: boolean;
  usesTablet: boolean;
  enableCockpit: boolean;
  staffNumber: string;
  email: string;
  mobilePhone: string;
  stationaryPhone: string;
  postalCode: string;
  userLocation: string;
  userAddressStreet: string;
  userAddressHousenumber: string;
  userCountry: string;
  vehicleId: string | undefined;
  vehicleAtHome: boolean;
  validationLevel: ValidationLevel;
  branchesChanged: boolean;
  supervisorId: string | null;
  coordinates: GeoLocation | null;
  temporaryPassword: string;
  hasLicencesChanged: boolean;
  hasLanguageChanged: boolean;
  homeClient: string;
  memberClientIds: string[];
}

/** Original user values for comparison. */
export interface UserOriginalValues {
  nickname: string;
  firstName: string;
  lastName: string;
  role: Role;
  wasSetToAdmin: boolean;
  usesTablet: boolean;
  hasEnabledCockpit: boolean;
  staffNumber: string;
  emailAddress: string;
  mobilePhone: string;
  stationaryPhone: string;
  postalCode: string;
  location: string;
  addressStreet: string;
  addressHousenumber: string;
  country: string;
  vehicleId: string | undefined;
  vehicleAtHome: boolean;
  validationLevel: ValidationLevel;
  supervisorId: string | null;
  coordinates: GeoLocation | null;
  homeClient: string;
  clients: string[];
  /** Whether `adminRightsAccess(user)` returned true — used as fallback for wasSetToAdmin. */
  adminRightsFallback: boolean;
}

/**
 * Determines whether any form field has changed compared to the original user.
 *
 * @param snapshot - Current form state.
 * @param original - Original user values loaded when the dialog opened.
 * @returns `true` if any field differs.
 */
export function hasFormDataChanged(
  snapshot: UserFormSnapshot,
  original: UserOriginalValues
): boolean {
  return (
    snapshot.nickname !== original.nickname ||
    snapshot.firstName !== original.firstName ||
    snapshot.lastName !== original.lastName ||
    snapshot.role !== original.role ||
    snapshot.wasSetToAdmin !== (original.wasSetToAdmin ?? original.adminRightsFallback) ||
    snapshot.usesTablet !== original.usesTablet ||
    snapshot.enableCockpit !== original.hasEnabledCockpit ||
    snapshot.staffNumber !== original.staffNumber ||
    snapshot.email !== original.emailAddress ||
    snapshot.mobilePhone !== original.mobilePhone ||
    snapshot.stationaryPhone !== original.stationaryPhone ||
    snapshot.postalCode !== original.postalCode ||
    snapshot.userLocation !== original.location ||
    snapshot.userAddressStreet !== original.addressStreet ||
    snapshot.userAddressHousenumber !== original.addressHousenumber ||
    snapshot.userCountry !== original.country ||
    snapshot.vehicleId !== original.vehicleId ||
    snapshot.vehicleAtHome !== original.vehicleAtHome ||
    snapshot.validationLevel !== original.validationLevel ||
    snapshot.branchesChanged ||
    snapshot.supervisorId !== original.supervisorId ||
    hasCoordinatesChanged(snapshot.coordinates, original.coordinates) ||
    snapshot.temporaryPassword !== "" ||
    snapshot.hasLicencesChanged ||
    snapshot.hasLanguageChanged ||
    snapshot.homeClient !== original.homeClient ||
    !arraysEqual(original.clients, snapshot.memberClientIds)
  );
}

// ─── Member client sorting ──────────────────────────────────────────

/**
 * Sorts member clients: selected clients first, then alphabetically by name.
 *
 * @param clients - All clients info from the group.
 * @param selectedClientIds - Currently selected member client IDs.
 * @returns Sorted array of `ClientsInfoOfGroup`.
 */
export function sortMemberClients(
  clients: ClientsInfoOfGroup[],
  selectedClientIds: string[]
): ClientsInfoOfGroup[] {
  return [...clients].sort((a, b) => {
    const nameA = (a.companyName || a.clientName) ?? "";
    const nameB = (b.companyName || b.clientName) ?? "";

    const isASelected = selectedClientIds.includes(a.clientId);
    const isBSelected = selectedClientIds.includes(b.clientId);

    if (isASelected && !isBSelected) return -1;
    if (!isASelected && isBSelected) return 1;

    return nameA.localeCompare(nameB);
  });
}

// ─── Group membership reconciliation ─────────────────────────────────

/** The membership values the form should work with, reconciled against the current group. */
export interface ReconciledGroupMembership {
  memberClientIds: string[];
  homeClient: string;
}

/**
 * Reconciles a user's stored group membership against the clients currently in the group.
 *
 * A user's `clients` array can reference a client that has since left the group (removing a
 * member client used not to clean up user data). Such an id has no matching option in the picker,
 * so it renders as an empty entry, and loading it raw means the next save writes it back and the
 * departed client keeps receiving updates for this user. Reconciling on load stops the form from
 * displaying or re-saving one.
 *
 * The stored order is preserved so the result compares equal to an untouched array.
 *
 * @param storedClients - `user.clients` as persisted.
 * @param storedHomeClient - `user.homeClient` as persisted.
 * @param groupClientIds - Clients currently in the group; empty for a client without a group.
 * @param currentClientId - The logged-in client, which is always kept.
 */
export function reconcileGroupMembership(
  storedClients: string[] | undefined,
  storedHomeClient: string | undefined,
  groupClientIds: string[],
  currentClientId: string
): ReconciledGroupMembership {
  const stored = storedClients ?? [currentClientId];

  // No group to reconcile against (standalone client): leave the stored values alone rather
  // than filtering everything away.
  if (groupClientIds.length === 0) {
    return {
      memberClientIds: stored,
      homeClient: storedHomeClient ?? currentClientId,
    };
  }

  const group = new Set(groupClientIds);
  const kept = [...new Set(stored.filter((clientId) => group.has(clientId)))];

  if (!kept.includes(currentClientId)) {
    kept.push(currentClientId);
  }

  return {
    memberClientIds: kept,
    homeClient:
      storedHomeClient && group.has(storedHomeClient)
        ? storedHomeClient
        : currentClientId,
  };
}

// ─── Disabled options for member client picker ───────────────────────

/**
 * Computes which member client options should be disabled in the picker.
 * The current client and the home client are always disabled (cannot be deselected).
 *
 * @param currentClientId - The logged-in user's client ID.
 * @param homeClient - The user's home client ID.
 * @returns Array of client IDs that should be disabled.
 */
export function getDisabledMemberClientOptions(
  currentClientId: string,
  homeClient: string
): string[] {
  if (homeClient !== currentClientId) {
    return [currentClientId, homeClient];
  }
  return [currentClientId];
}

// ─── Save-button disabled logic ──────────────────────────────────────

/**
 * Determines whether the save button should be disabled.
 *
 * The save button is disabled when:
 * - No data has changed AND the form is NOT for user creation
 *   (or for user creation but no temporary password has been entered)
 *
 * @param hasDataChanged - Whether any form field has changed.
 * @param startedForUserCreation - Whether the dialog is in creation mode.
 * @param temporaryPassword - The temporary password entered.
 * @returns `true` if save should be disabled.
 */
export function isSaveButtonDisabled(
  hasDataChanged: boolean,
  startedForUserCreation: boolean,
  temporaryPassword: string
): boolean {
  return !hasDataChanged && (!startedForUserCreation || temporaryPassword === "");
}

// ─── Timestamp helpers ──────────────────────────────────────────────

/**
 * Converts nanoseconds to seconds.
 */
export function nanosecondsToSeconds(nanoseconds: number): number {
  return nanoseconds / 1_000_000_000;
}

/**
 * Converts seconds to milliseconds.
 */
export function secondsToMilliseconds(seconds: number): number {
  return seconds * 1000;
}

/**
 * Builds a JS Date from Firestore-style `{seconds, nanoseconds}` values.
 *
 * @returns A Date object, or `null` if seconds/nanoseconds are falsy.
 */
export function buildDateFromTimestampParts(
  seconds: number | undefined,
  nanoseconds: number | undefined
): Date | null {
  if (!seconds || !nanoseconds) {
    return null;
  }
  const totalMs = secondsToMilliseconds(seconds + nanosecondsToSeconds(nanoseconds));
  return new Date(totalMs);
}

// ─── Vehicle display name ───────────────────────────────────────────

/**
 * Returns the display name for a vehicle (licence plate preferred, then vehicleId).
 */
export function getVehicleDisplayName(vehicle: Vehicle | null | undefined): string | null {
  return vehicle?.licencePlate ?? vehicle?.vehicleId ?? null;
}

// ─── Vehicle allocation diff ────────────────────────────────────────

/**
 * Computes the vehicle updates needed when a user's vehicle assignment changes.
 *
 * @param newVehicleId - The ID of the newly selected vehicle (or null).
 * @param previousVehicleId - The ID of the previously assigned vehicle (or undefined).
 * @param userId - The user's ID (to set as allocatedUser).
 * @param allVehicles - All vehicles of the client.
 * @returns An object with `toAdd` and `toRemove` vehicles with updated allocatedUser.
 */
export function computeVehicleAllocationChanges(
  newVehicleId: string | null | undefined,
  previousVehicleId: string | undefined,
  userId: string,
  allVehicles: Vehicle[]
): { toAdd: Vehicle | null; toRemove: Vehicle | null } {
  if (newVehicleId === previousVehicleId) {
    return { toAdd: null, toRemove: null };
  }

  const toAdd = newVehicleId
    ? allVehicles.find((v) => v.id === newVehicleId) ?? null
    : null;

  const toRemove = previousVehicleId
    ? allVehicles.find((v) => v.id === previousVehicleId) ?? null
    : null;

  return {
    toAdd: toAdd ? { ...toAdd, allocatedUser: userId } : null,
    toRemove: toRemove ? { ...toRemove, allocatedUser: null } : null,
  };
}

// ─── Shared user detection ──────────────────────────────────────────

/**
 * Determines whether a user is a "shared user" (belongs to multiple clients).
 *
 * A user is considered shared if:
 * - They have the `sharedUserOfGroup` flag, OR
 * - They have a `sharedEmailAddressId`, OR
 * - They are currently assigned to more than one client in the form.
 */
export function isUserShared(
  user: UserFormUser | null,
  memberClientIds: string[]
): boolean {
  return !!(
    user?.sharedUserOfGroup ||
    user?.sharedEmailAddressId ||
    memberClientIds.length > 1
  );
}

// ─── Email address field helpers ─────────────────────────────────────

/**
 * Determines if the shared email address button color.
 *
 * @returns "disabled", "green", or "red" status string.
 */
export function getSharedEmailButtonStatus(
  hasEmailValueChanged: boolean,
  sharedEmailAddressId: string | null,
  isEditDisabled: boolean
): "disabled" | "green" | "red" {
  if (hasEmailValueChanged) {
    return "disabled";
  }
  if (sharedEmailAddressId) {
    return isEditDisabled ? "disabled" : "green";
  }
  return "red";
}

/**
 * Whether a failed user save was refused by IRES Cloud (`permission-denied`), as opposed to failing.
 * The Firebase SDK reports the code with or without the `functions/` prefix depending on its
 * version, so both are accepted. Since 2026-10-01 a refusal mostly means the admin portal client's
 * rule: only a named SysAdmin may change roles or create users there.
 *
 * @param error - whatever the callable rejected with.
 * @returns true for a refusal.
 */
export function isRefusedUserSave(error: unknown): boolean {
  const code = (error as { code?: unknown } | null)?.code;
  return code === "permission-denied" || code === "functions/permission-denied";
}
